/**
 * MCP (Model Context Protocol) 命令
 * 管理 MCP 服务器的启动、停止和配置
 */

import { Command } from 'commander';
import chalk from 'chalk';
import ora from 'ora';
import path from 'path';
import {
  startMCPServerStdio,
  startMCPServerSSE,
  isMCPServerRunning,
  generateClaudeConfig,
  testMCPConnection,
  MCPServerProcess,
} from '../utils/mcp-manager.js';
import { logger } from '../utils/logger.js';

// 全局 MCP 服务器实例
let mcpServerInstance: MCPServerProcess | null = null;

export function createMcpCommand(): Command {
  const command = new Command('mcp');
  
  command
    .description('Manage Model Context Protocol (MCP) server')
    .option('-m, --mode <mode>', 'Transport mode (stdio or sse)', 'stdio')
    .option('-p, --port <port>', 'SSE server port', '3457')
    .option('-w, --workspace <path>', 'Workspace path', process.cwd())
    .action(async (options) => {
      const mode = options.mode.toLowerCase();
      
      if (mode !== 'stdio' && mode !== 'sse') {
        console.error(chalk.red('Invalid mode. Use "stdio" or "sse"'));
        process.exit(1);
      }
      
      console.log(chalk.blue.bold(`\n🤖 Workflow MCP Server (${mode.toUpperCase()} mode)\n`));
      
      try {
        const port = parseInt(options.port);
        const workspace = path.resolve(options.workspace);
        
        if (mode === 'sse') {
          // SSE 模式
          const server = await startMCPServerSSE({
            mode: 'sse',
            port,
            workspace,
          });
          
          mcpServerInstance = server;
          
          console.log(chalk.cyan(`
📡 MCP Server Information:
  - Mode: SSE (Server-Sent Events)
  - Port: ${port}
  - Workspace: ${workspace}
  
🔗 Endpoints:
  - SSE Connection: http://localhost:${port}/mcp/sse
  - Send Message: http://localhost:${port}/mcp/sse/message
  - Health Check: http://localhost:${port}/health
  
Press Ctrl+C to stop the server
          `));
          
        } else {
          // STDIO 模式
          const server = await startMCPServerStdio({
            mode: 'stdio',
            workspace,
          });
          
          mcpServerInstance = server;
          
          console.log(chalk.cyan(`
🔌 MCP Server Information:
  - Mode: STDIO
  - Workspace: ${workspace}
  
Configure Claude Desktop:
${generateClaudeConfig(workspace)}

Save this configuration to your Claude Desktop settings.

Press Ctrl+C to stop the server
          `));
        }
        
        // 设置优雅关闭
        process.on('SIGINT', () => {
          if (mcpServerInstance?.process) {
            console.log(chalk.yellow('\nStopping MCP server...'));
            mcpServerInstance.process.kill('SIGINT');
          }
          process.exit(0);
        });
        
        // 保持进程运行
        await new Promise(() => {});
        
      } catch (error: any) {
        console.error(chalk.red('Failed to start MCP server:'), error.message);
        process.exit(1);
      }
    });
  
  // 测试 MCP 连接
  command
    .command('test')
    .description('Test MCP server connection')
    .option('-u, --url <url>', 'MCP SSE server URL', 'http://localhost:3457/health')
    .action(async (options) => {
      const spinner = ora('Testing MCP connection...').start();
      
      try {
        const isConnected = await testMCPConnection(options.url);
        
        if (isConnected) {
          spinner.succeed('MCP server connection successful');
        } else {
          spinner.fail('MCP server connection failed');
        }
      } catch (error: any) {
        spinner.fail(`Connection error: ${error.message}`);
      }
    });
  
  // 显示配置
  command
    .command('config')
    .description('Show Claude Desktop configuration')
    .option('-w, --workspace <path>', 'Workspace path', process.cwd())
    .action((options) => {
      const workspace = path.resolve(options.workspace);
      
      console.log(chalk.blue.bold('\n📋 Claude Desktop Configuration\n'));
      console.log('Add this to your Claude Desktop settings:\n');
      console.log(chalk.cyan(generateClaudeConfig(workspace)));
      console.log('\nLocation:');
      console.log(chalk.gray('  macOS: ~/Library/Application Support/Claude/claude_desktop_config.json'));
      console.log(chalk.gray('  Windows: %APPDATA%\\Claude\\claude_desktop_config.json'));
      console.log(chalk.gray('  Linux: ~/.config/Claude/claude_desktop_config.json'));
      console.log();
    });
  
  // 显示可用工具
  command
    .command('tools')
    .description('List available MCP tools')
    .action(() => {
      console.log(chalk.blue.bold('\n🛠️  Available MCP Tools\n'));
      
      const tools = [
        { name: 'create_task', description: 'Create a new development task' },
        { name: 'update_task_status', description: 'Update task status (todo/doing/review/done)' },
        { name: 'generate_prd', description: 'Generate PRD from an idea' },
        { name: 'sync_to_ide', description: 'Sync workspace config to IDE' },
        { name: 'manage_documents', description: 'Create and manage project documents' },
        { name: 'search_documents', description: 'Search through project documentation' },
      ];
      
      const maxNameLength = Math.max(...tools.map(t => t.name.length));
      
      tools.forEach(tool => {
        const name = tool.name.padEnd(maxNameLength);
        console.log(`  ${chalk.green(name)}  ${tool.description}`);
      });
      
      console.log('\n' + chalk.gray('Use these tools through Claude Desktop or other MCP-compatible clients.'));
      console.log();
    });
  
  // 显示状态
  command
    .command('status')
    .description('Check MCP server status')
    .option('-p, --port <port>', 'SSE server port', '3457')
    .action(async (options) => {
      console.log(chalk.blue.bold('\n📊 MCP Server Status\n'));
      
      const port = parseInt(options.port);
      
      // 检查 SSE 服务器
      const spinner = ora('Checking SSE server...').start();
      const isRunning = await isMCPServerRunning(port);
      
      if (isRunning) {
        spinner.succeed(`SSE server is running on port ${port}`);
        console.log(chalk.gray(`  Health check: http://localhost:${port}/health`));
      } else {
        spinner.fail(`SSE server is not running on port ${port}`);
      }
      
      console.log();
    });
  
  return command;
}